import { Cart, Order, OrderContainer } from '../types/trpc';
import { FinalizedOrderLineItems, ContainerBreakdown } from '@libs/schemas';
import { calculateContainerBreakdown } from './container-breakdown';

// Generic types that work for both admin and consumer router outputs
export interface ChargeSummaryProps<TOrder = Order, TCart = Cart, TContainer = OrderContainer> {
  order: TOrder;
  cart: TCart;
  weightCostAdjInCents?: number;
  materialCostAdjInCents?: number;
  containerCostCents?: number;
  finalWeight?: number;
  additionalMaterials?: { [materialId: string]: number };
  containers?: TContainer[];
  usageByContainer?: { [containerId: string]: number };
  containerBreakdown?: ContainerBreakdown[];
}

/**
 * Extracts ChargeSummary props from completed order's finalizedLineItems
 * Use this for displaying charge summary of completed/refunded orders
 *
 * @param order - The order with finalizedLineItems
 * @param cart - The cart associated with the order
 * @param containers - Array of actual container objects with full info
 * @param containerBreakdown - Optional pre-calculated breakdown from backend
 * @returns ChargeSummaryProps or null if no finalizedLineItems
 */
export function extractChargeSummaryPropsFromFinalizedOrder<
  TOrder extends { finalizedLineItems?: FinalizedOrderLineItems },
  TCart,
  TContainer extends { _id?: string },
>(
  order: TOrder,
  cart: TCart,
  containers: TContainer[],
  containerBreakdown?: ContainerBreakdown[],
): ChargeSummaryProps<TOrder, TCart, TContainer> | null {
  if (!order.finalizedLineItems) {
    return null;
  }

  const { finalizedLineItems } = order;

  return {
    order,
    cart,
    // Pre-calculated adjustment amounts from backend
    weightCostAdjInCents: finalizedLineItems.weight.adjustCostInCents,
    materialCostAdjInCents: finalizedLineItems.additionalMaterialChargesAdjustedCostInCents,
    containerCostCents: finalizedLineItems.containersAdjustedCostInCents,

    // Actual values used during waste processing
    finalWeight: finalizedLineItems.weight.tons,

    // Extract usageByContainer from finalizedLineItems (kept for backward compatibility)
    usageByContainer: finalizedLineItems.containers.reduce(
      (
        acc: { [containerId: string]: number },
        c: FinalizedOrderLineItems['containers'][number],
      ) => ({ ...acc, [c.containerId]: c.days }),
      {} as { [containerId: string]: number },
    ),

    // Extract additionalMaterials from finalizedLineItems
    additionalMaterials: finalizedLineItems.additionalMaterialCharges.reduce(
      (
        acc: { [materialId: string]: number },
        m: FinalizedOrderLineItems['additionalMaterialCharges'][number],
      ) => ({ ...acc, [m.materialId]: m.quantity }),
      {} as { [materialId: string]: number },
    ),

    // Pass containers for backward compatibility
    containers: containers as TContainer[],

    // Pass backend-calculated breakdown (single source of truth)
    containerBreakdown,
  };
}

/**
 * Creates ChargeSummary props from waste processing state
 * Use this during waste processing flow
 *
 * @param order - The order being processed
 * @param cart - The cart associated with the order
 * @param adjustments - Calculated adjustment amounts from getPricingAwareOrderAdjustments
 * @param state - Current waste processing state
 * @param containers - Array of actual container objects
 * @returns ChargeSummaryProps
 */
export function createChargeSummaryPropsFromWasteProcessing<
  TOrder extends {
    quotedPricingDetails?: {
      quote?: {
        pricing?: {
          containers?: Array<{
            includedDays?: number;
            adjustedPriceInCents?: number;
            dailyOverageFeeInCents?: number;
            totalDays?: number;
            periodTier?: {
              selectedPeriodDays?: number;
              additionalDayRateInCents?: number;
              dayOverageInCents?: number;
            };
          }>;
        };
      };
    };
  },
  TCart extends {
    lineItems?: { containers?: Array<{ totalDays?: number; totalInCents?: number }> };
  },
  TContainer extends { _id?: string; containerTypeInfo?: { size: string; name?: string } },
>(
  order: TOrder,
  cart: TCart,
  adjustments: {
    weightAdjustment: number;
    materialsAdjustment: number;
    containerAdjustment: number;
    containerRefundDue?: number;
  },
  state: {
    weight: number;
    additionalMaterials: { [materialId: string]: number };
    usageByContainer: { [containerId: string]: number };
  },
  containers: TContainer[],
): ChargeSummaryProps<TOrder, TCart, TContainer> {
  // Extract pricing details from order for container breakdown calculation
  const containerPricing = order.quotedPricingDetails?.quote?.pricing?.containers?.[0];
  const periodTier = containerPricing?.periodTier;

  // Determine included days based on pricing model
  const includedDays = periodTier?.selectedPeriodDays || containerPricing?.includedDays || 0;

  // Determine daily rate for additional days
  const dailyRateInCents =
    periodTier?.additionalDayRateInCents || // Bundle: additional day rate
    periodTier?.dayOverageInCents ||        // Bundle: day overage rate (alternative field)
    containerPricing?.dailyOverageFeeInCents || // Fixed: daily overage fee
    containerPricing?.adjustedPriceInCents || // Fallback
    0;

  // Get total prepaid days from cart (for refund calculation)
  const totalDaysPaidAtCheckout =
    containerPricing?.totalDays ||
    cart.lineItems?.containers?.reduce((sum, c) => sum + (c.totalDays || 0), 0) ||
    0;

  // Calculate container breakdown for display
  const containerBreakdown =
    includedDays > 0 || dailyRateInCents > 0
      ? calculateContainerBreakdown(
          containers,
          state.usageByContainer,
          includedDays,
          dailyRateInCents,
          totalDaysPaidAtCheckout,
        )
      : undefined;

  return {
    order,
    cart,
    // Calculated adjustment amounts
    weightCostAdjInCents: adjustments.weightAdjustment,
    materialCostAdjInCents: adjustments.materialsAdjustment,
    containerCostCents: adjustments.containerAdjustment - (adjustments.containerRefundDue || 0),

    // Current state values
    finalWeight: state.weight,
    additionalMaterials: state.additionalMaterials,
    usageByContainer: state.usageByContainer,
    containers,

    // Calculated breakdown for display
    containerBreakdown,
  };
}

/**
 * Calculate container overages with refund logic (admin view)
 * Used during waste processing when handling prepaid days and refunds
 *
 * @param containers - Array of container objects
 * @param usageByContainer - Map of container ID to actual days used
 * @param config - Configuration with includedDays, dailyRateInCents, totalDaysPaidAtCheckout
 * @returns Array of detailed container overages with refund calculations
 */
export function calculateContainerOveragesWithRefunds<
  TContainer extends { _id?: string; containerTypeInfo?: { size: string } },
>(
  containers: TContainer[],
  usageByContainer: { [containerId: string]: number },
  config: {
    includedDays: number;
    dailyRateInCents: number;
    totalDaysPaidAtCheckout: number;
  },
): Array<{
  containerId: string;
  containerSize?: string;
  usedDays: number;
  includedDays: number;
  daysOver: number;
  extraDaysPaid: number;
  refundDays: number;
  refundAmount: number;
  additionalDays: number;
  additionalCost: number;
}> {
  const extraDaysPaidAtCheckout = Math.max(0, config.totalDaysPaidAtCheckout - config.includedDays);

  return containers.map((container) => {
    const usedDays = usageByContainer[container._id || ''] || 0;

    // Calculate days beyond included
    const daysOver = Math.max(0, usedDays - config.includedDays);

    // Calculate refund or additional charge (mutually exclusive)
    const refundDays = Math.max(0, extraDaysPaidAtCheckout - daysOver);
    const additionalDays = Math.max(0, daysOver - extraDaysPaidAtCheckout);

    const refundAmount = refundDays * config.dailyRateInCents;
    const additionalCost = additionalDays * config.dailyRateInCents;

    return {
      containerId: container._id || '',
      containerSize: container.containerTypeInfo?.size,
      usedDays,
      includedDays: config.includedDays,
      daysOver,
      extraDaysPaid: Math.min(daysOver, extraDaysPaidAtCheckout),
      refundDays,
      refundAmount,
      additionalDays,
      additionalCost,
    };
  });
}

/**
 * Calculate container overages without refund logic (web/customer view)
 * Used when displaying completed orders without refund details
 *
 * @param containers - Array of container objects
 * @param usageByContainer - Map of container ID to actual days used
 * @param config - Configuration with includedDays and dailyRateInCents
 * @returns Array of simplified container overages
 */
export function calculateContainerOverages<
  TContainer extends { _id?: string; containerTypeInfo?: { size: string } },
>(
  containers: TContainer[],
  usageByContainer: { [containerId: string]: number },
  config: {
    includedDays: number;
    dailyRateInCents: number;
  },
): Array<{
  containerId: string;
  containerSize?: string;
  usedDays: number;
  includedDays: number;
  additionalDays: number;
  additionalCost: number;
}> {
  return containers.map((container) => {
    const usedDays = usageByContainer[container._id || ''] || 0;
    const additionalDays = Math.max(0, usedDays - config.includedDays);
    const additionalCost = additionalDays * config.dailyRateInCents;

    return {
      containerId: container._id || '',
      containerSize: container.containerTypeInfo?.size,
      usedDays,
      includedDays: config.includedDays,
      additionalDays,
      additionalCost,
    };
  });
}
